import { createContext, useContext, useState } from 'react'

import type { StatusVariant } from '@factorialco/f0-react'
import type { MarketplaceIntegrationWithIdsFragment } from 'generated/resources/graphql'
import {
  CreateIntegrationsSyncRunParametersInput,
  CreateSyncRunDocument,
  CreateSyncRunMutation,
  ExpensablesDataCollectionQuery,
  IntegrationsAgreggatedSyncableStateValuesEnum,
  IntegrationsCapabilityModeEnum,
  IntegrationsSyncRunFragment,
  IntegrationsSyncRunOriginEnum,
  IntegrationsSyncableTypeEnum,
} from 'generated/resources/graphql'
import { print } from 'graphql'
import type { DocumentNode } from 'graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { GraphqlDataCollectionMappedVariables } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'

import { EmployeePayrollRunNode } from 'modules/compensations/components/EmployeePayrollRunElements'
import { SyncableStateAggregation } from 'modules/integrations/components/SyncFooter/LastSync/Syncing/StatusAggregation'
import { useAvailableIntegration } from 'modules/integrations/hooks/SyncContext/availableIntegration'
import { useSyncRunStatusChangedSubscription } from 'modules/integrations/hooks/SyncContext/subscriptions/useSyncRunStatusChangedSubscription'
import { useSyncableStateUpdatedSubscription } from 'modules/integrations/hooks/SyncContext/subscriptions/useSyncableStateUpdatedSubscription'
import {
  useReadLastSyncRun,
  useUpdateLastSyncRunQueryCache,
} from 'modules/integrations/hooks/SyncContext/useLastSyncRun'
import { SyncRunStatus, setSyncRunStatusIdSessionStorage } from 'modules/integrations/lib/syncRun'

type ExpensesExpensableNode =
  ExpensablesDataCollectionQuery['expenses']['expensablesConnection']['nodes'][number]

export type SyncStatusColumnItem = ExpensesExpensableNode | EmployeePayrollRunNode

// Accept any GraphqlDataCollectionSource variant regardless of type parameters
// since we only use mappedVariables which is common to all variants
export type AnyGraphqlDataCollectionSource = {
  mappedVariables?: GraphqlDataCollectionMappedVariables
  document?: DocumentNode
  [key: string]: unknown
}

export interface SyncParameter {
  key: string
  value: string
}

export interface DataCollectionMappedVariable {
  key: string
  value: string
}

export type ContextParameters = Record<string, string>

export type DataCollectionMappedVariables = DataCollectionMappedVariable[]

export interface Configuration {
  origin: IntegrationsSyncRunOriginEnum
  syncableType: IntegrationsSyncableTypeEnum
  syncableStatesAggregation?: SyncableStateAggregation
  syncableStatusesFilter?: IntegrationsAgreggatedSyncableStateValuesEnum[]
}

export type SyncStatusColumn = {
  label: string
  render: (item: SyncStatusColumnItem) => SyncStatusColumnValue | undefined
}

export type SyncStatusColumnValue = {
  type: 'status'
  value: {
    status: StatusVariant
    label: string
  }
}

export interface SyncContextType {
  isSyncRunning: boolean
  setContextParameters: (parameters: ContextParameters) => void
  marketplaceIntegration: MarketplaceIntegrationWithIdsFragment | null
  syncEnabled: boolean
  lastSyncRun?: IntegrationsSyncRunFragment | null
  setDataCollectionSource: (source: AnyGraphqlDataCollectionSource) => void
}

export interface PrivateSyncContextType {
  runSynchronisation: () => Promise<void>
  isSyncRunCreating: boolean
  isSyncRunning: boolean
  contextParameters: ContextParameters
  marketplaceIntegration: MarketplaceIntegrationWithIdsFragment | null
  syncEnabled: boolean
  lastSyncRun?: IntegrationsSyncRunFragment | null
  getDataCollectionSource: () => AnyGraphqlDataCollectionSource | null
  configuration: Configuration
  capabilityMode?: IntegrationsCapabilityModeEnum | null
}

const SyncContext = createContext<SyncContextType | null>(null)
const PrivateSyncContext = createContext<PrivateSyncContextType | null>(null)

let dataCollectionSource: AnyGraphqlDataCollectionSource | null = null

const setDataCollectionSource = (source: AnyGraphqlDataCollectionSource) => {
  dataCollectionSource = source
}

export const IntegrationsSyncContextProvider = ({
  children,
  configuration,
}: {
  children: React.ReactNode
  configuration: Configuration
}) => {
  const {
    company: { id: companyId },
  } = useCurrentSession()

  const [contextParameters, setContextParameters] = useState<ContextParameters>({})

  const availableIntegration = useAvailableIntegration(configuration.origin)

  const marketplaceIntegration = availableIntegration?.marketplaceIntegration ?? null
  const capabilityMode = availableIntegration?.capabilityMode ?? null

  const syncEnabled = !!marketplaceIntegration

  const updateLastSyncRunQueryCache = useUpdateLastSyncRunQueryCache()

  const [sync, { loading: isSyncRunCreating }] = useMutation(CreateSyncRunDocument, {
    extractorFn: (data: CreateSyncRunMutation) => {
      return data.integrationsMutations.createIntegrationsSyncRun
    },
    update(_, { data }) {
      const syncRun = data?.integrationsMutations?.createIntegrationsSyncRun?.syncRun
      if (!syncRun || !marketplaceIntegration) return

      // Update the LastSyncRun query cache with the new created sync run
      // instead refetching to avoid DBs replication lag issues
      updateLastSyncRunQueryCache({
        marketplaceIntegration,
        contextParameters,
        syncRun,
        origin: configuration.origin,
      })
    },
  })

  const convertMappedVariablesToParameters = (
    mappedVariables: GraphqlDataCollectionMappedVariables
  ): CreateIntegrationsSyncRunParametersInput[] => {
    if (!mappedVariables) return []

    return Object.entries(mappedVariables)
      .map(
        ([key, value]: [string, unknown]): CreateIntegrationsSyncRunParametersInput | undefined => {
          let stringValue: string | undefined

          if (Array.isArray(value) && value.length > 0) {
            stringValue = value.join(',')
          } else if (['string', 'number', 'boolean'].includes(typeof value)) {
            stringValue = String(value)
          }

          if (!stringValue) return undefined

          const snakeCaseKey = key
            .replace(/([A-Z])/g, '_$1')
            .toLowerCase()
            .replace(/^_/, '')

          return {
            key: snakeCaseKey,
            value: stringValue,
          }
        }
      )
      .filter((param): param is CreateIntegrationsSyncRunParametersInput => param !== undefined)
  }

  const { syncRunOutputsLoading } = useSyncRunStatusChangedSubscription({
    marketplaceIntegration,
    syncEnabled,
    contextParameters,
    origin: configuration.origin,
  })

  useSyncableStateUpdatedSubscription({
    marketplaceIntegration,
    syncEnabled,
  })

  const runSynchronisation = async () => {
    if (!syncEnabled || !marketplaceIntegration) return

    const mappedVariables = dataCollectionSource?.mappedVariables
    const queryForSync = dataCollectionSource?.document
      ? print(dataCollectionSource.document)
      : null

    const result = await sync({
      variables: {
        companyId,
        marketplaceIntegrationId: marketplaceIntegration.id,
        origin: configuration.origin,
        // TODO: IntegrationsFramework. rename parameters to contextParameters and add only the ones needed (i.e payrollRunId)
        parameters: convertMappedVariablesToParameters(dataCollectionSource?.mappedVariables),
        graphqlVariablesJson: JSON.stringify({
          query: queryForSync,
          variables: { ...mappedVariables },
        }),
      },
    })

    const handledResult = handleResult(result, () => {})
    if (handledResult.success) {
      const syncRunId = handledResult?.data?.syncRun?.id
      if (syncRunId) {
        setSyncRunStatusIdSessionStorage(syncRunId.toString())
      }
    }
  }

  const { data: lastSyncRunData } = useReadLastSyncRun({
    marketplaceIntegration,
    contextParameters,
    origin: configuration.origin,
    syncEnabled,
  })

  const lastSyncRun = lastSyncRunData?.integrations?.syncRunsConnection.nodes[0] || null

  const getDataCollectionSource = () => {
    return dataCollectionSource
  }

  const isSyncRunning =
    isSyncRunCreating || lastSyncRun?.status === SyncRunStatus.RUNNING || syncRunOutputsLoading

  return (
    <PrivateSyncContext.Provider
      value={{
        runSynchronisation,
        isSyncRunCreating,
        isSyncRunning,
        contextParameters,
        marketplaceIntegration,
        syncEnabled,
        lastSyncRun,
        getDataCollectionSource,
        configuration,
        capabilityMode,
      }}
    >
      <SyncContext.Provider
        value={{
          isSyncRunning,
          setContextParameters,
          marketplaceIntegration,
          syncEnabled,
          lastSyncRun,
          setDataCollectionSource,
        }}
      >
        {children}
      </SyncContext.Provider>
    </PrivateSyncContext.Provider>
  )
}

export function useIntegrationsSyncContext(): SyncContextType {
  const context = useContext(SyncContext)

  if (!context) {
    throw new Error('useIntegrationsSyncContext must be used within a SyncContextProvider')
  }

  return context
}

// As react hooks cannot be conditional, we need to use a flag to disable the context
export function usePrivateConditionalIntegrationsSyncContext(
  disabled = false
): PrivateSyncContextType | undefined {
  const context = useContext(PrivateSyncContext)
  if (disabled) return undefined

  if (!context) {
    throw new Error(
      'useConditionalIntegrationsSyncContext must be used within a SyncContextProvider'
    )
  }

  return context
}

export function usePrivateIntegrationsSyncContext() {
  const context = useContext(PrivateSyncContext)

  if (!context) {
    throw new Error(
      'usePrivateIntegrationsSyncContext must be used within a PrivateSyncContextProvider'
    )
  }

  return context
}
